import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import componentStyles from './tufts-yext-poc.css.lit';
import { Task } from '@lit-labs/task';
import '../tufts-yext-item/tufts-yext-item';
// import { Task, TaskStatus } from '@lit-labs/task';

export type verticalSearchResponseStructure = {
  businessId: number;
  queryId: string;
  resultsCount: number;
  results: {
    data: verticalSearchResultsDataStructure;
    highlightedFields: Record<string, unknown>;
    distance: number;
    distanceFromFilter: number;
  }[];
  appliedQueryFilters: unknown[];
  facets: {
    fieldId: string;
    displayName: string;
    options: unknown[];
  }[];
  searchIntents: unknown[];
  source: string;
  directAnswer: Record<string, unknown>;
  alternativeVerticals: Record<string, unknown>;
  spellCheck: Record<string, unknown>;
  locationBias: Record<string, unknown>;
  allResultsForVertical: Record<string, unknown>;
};

export type verticalSearchResultsDataStructure = {
  address: {
    line1: string;
    city: string;
    region: string;
    postalCode: string;
    countryCode: string;
  };
  cityCoordinate: {
    latitude: number;
    longitude: number;
  };
  fax: string;
  firstName: string;
  geocodedCoordinate: {
    latitude: number;
    longitude: number;
  };
  id: string;
  isoRegionCode: string;
  lastName: string;
  mainPhone: string;
  name: string;
  npi: string;
  timeZoneUtcOffset: string;
  timezone: string;
  type: string;
  uid: string;
};

/**
 * The Yext component.
 * @element tufts-yext-poc
 */
@customElement('tufts-yext-poc')
export class TuftsYextPoc extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  urlHref = 'https://cdn.yextapis.com/v2/accounts';
  accountId = 'me';
  contentType = 'answers/vertical/query';
  // apiKey = process.env.apiKey || '';
  apiKey = 'f70a1166dcbde8731713f74b5d21b86f';

  apiVersion = '20230406';
  experienceKey = 'tufts-fad';
  verticalKey = 'healthcare_professionals';
  version = 'PRODUCTION';
  locale = 'en';
  input = '';
  itemsLimit = '50';
  fields = 'firstName,lastName,data.id';
  offset = '0';
  urlBase = `${this.urlHref}/${this.accountId}/${this.contentType}`;

  totalCount: null | number;
  randomizationToken: null | string;

  createUrl() {
    const params = new URLSearchParams();
    // params.set('api_key', this.apiKey);
    params.set('v', this.apiVersion);
    params.set('experienceKey', this.experienceKey);
    params.set('verticalKey', this.verticalKey);
    params.set('version', this.version);
    params.set('locale', this.locale);
    params.set('input', this.input);
    // params.set('fields', this.fields);
    params.set('offset', this.offset);
    // params.set('entityTypes', this.entityTypes);
    // params.set('randomization', '1');
    params.set('limit', this.itemsLimit);
    return `${this.urlBase}?${params.toString()}`;
  }

  url = this.createUrl();
  lastFetchTime: null | number;

  @state()
  entities: [];

  _apiTask = new Task(
    this,
    async () => {
      const startTime = performance.now();
      const response = await fetch(this.url, {
        headers: {
          'api-key': this.apiKey,
        },
      });
      const jsonResponse = await response.json();
      const endTime = performance.now();
      this.lastFetchTime = (endTime - startTime) / 1000;
      return jsonResponse;
    },
    () => [this.entities]
  );

  setURLParams(entityId: string) {
    // use URLSearchParams to get the query string from window.location.href
    const params = new URLSearchParams(window.location.search);
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    params.set('yext', entityId);

    const stateObj = { id: '100' };
    window.history.pushState(
      stateObj,
      'Page',
      `${origin}${pathname}?${params.toString()}`
    );

    this.shadowRoot!.querySelector('tufts-yext-item')!.refresh();
  }

  // from https://hitchhikers.yext.com/docs/contentdeliveryapis/search/verticalsearch

  displayAll(response: verticalSearchResponseStructure) {
    this.totalCount = response.resultsCount;
    // this.randomizationToken = response.randomizationToken;

    return response.results.map(result => {
      return html`
        <div>
          ${result.data.firstName}, ${result.data.lastName},
          <button
            type="button"
            @click="${() => this.setURLParams(result.data.id)}"
          >
            ${result.data.id}
          </button>
        </div>
      `;
    });
  }

  loadNextPage() {
    // add itemsLimit to offset and return it as a string
    this.offset = (+this.offset + +this.itemsLimit).toString();
    this.url = this.createUrl();
    this._apiTask.run();
  }

  reset() {
    this.offset = '0';
    this.id = '';
    this.randomizationToken = null;
    this.url = this.createUrl();
    this._apiTask.run();
  }

  render(): TemplateResult {
    return html`
      <div class="wrapper">
        <div class="all-entities">
          <h2><code>tufts-yext-poc</code> component</h2>
          <button type="button" @click="${this.reset}">Reset</button>
          <button type="button" @click="${this.loadNextPage}">Next Page</button>
          ${this.totalCount
            ? html` <div>Offset: ${this.offset} out of ${this.totalCount}</div>`
            : null}
          ${this.lastFetchTime
            ? html`<div>Fetch time: ${this.lastFetchTime}</div>`
            : null}
          ${this._apiTask.render({
            pending: () =>
              html`<div class="lds-ring">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>`,
            complete: data => html` ${this.displayAll(data.response)} `,
            error: error => html`${error}`,
          })}
        </div>
        <div class="entity">
          <h2><code>tufts-yext-item</code> component</h2>
          <tufts-yext-item></tufts-yext-item>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-yext-poc': TuftsYextPoc;
  }
}
